// packages/web-ui/src/hooks/useSlashCommands.ts
//
// Story W2-S7 AC #7 — convenience hook that derives the slash-menu open
// state from textarea content and exposes a `Ctrl+P` listener for the
// global palette.
//
// Two responsibilities:
//   1. Detect when the textarea begins with `/` (after stripping leading
//      whitespace) → `slashOpen: true`, `query: '<text-after-slash>'`.
//   2. Listen for `Ctrl+P` / `Cmd+P` globally and toggle a separate
//      `globalPaletteOpen` flag.
//
// Both flags are returned controlled — the parent decides what to do with
// them (typically: thread them into `<SlashMenu>` / `<GlobalPalette>`
// `open` props).

import { useCallback, useEffect, useMemo, useState } from 'react';

export interface UseSlashCommandsOptions {
  /** Current textarea value. */
  value: string;
  /** Caret offset (used to suppress slash menu when caret is past the trigger). */
  caret?: number;
  /** Disable the global Ctrl+P listener (e.g. embedded inside another modal). */
  disableGlobalShortcut?: boolean;
  /** Document override for tests. */
  documentImpl?: Document;
}

export interface UseSlashCommandsResult {
  /** True when the inline slash menu should render. */
  slashOpen: boolean;
  /** Substring after the leading `/` (e.g. `gen` for `/gen`). */
  slashQuery: string;
  /** True when the centred-modal global palette should render. */
  globalPaletteOpen: boolean;
  /** Toggle helper for the global palette. */
  setGlobalPaletteOpen: (open: boolean) => void;
}

/**
 * Detect a slash trigger at the START of the value (after leading
 * whitespace). Returns the substring after the `/` if present, else
 * `null`.
 */
export function detectSlashTrigger(value: string): string | null {
  const trimmed = value.replace(/^\s+/, '');
  if (!trimmed.startsWith('/')) return null;
  // Slash menu only renders for the first command word — close once the
  // user types whitespace (a space introduces the parameter section, which
  // the parameter form handles instead).
  const firstWhitespace = trimmed.indexOf(' ');
  if (firstWhitespace > 0) return null;
  return trimmed.slice(1);
}

export function useSlashCommands(
  options: UseSlashCommandsOptions,
): UseSlashCommandsResult {
  const { value, disableGlobalShortcut = false, documentImpl } = options;

  const [globalPaletteOpen, setGlobalPaletteOpen] = useState(false);

  const slashState = useMemo(() => {
    const sub = detectSlashTrigger(value);
    return {
      slashOpen: sub !== null,
      slashQuery: sub ?? '',
    };
  }, [value]);

  useEffect(() => {
    if (disableGlobalShortcut) return undefined;
    const doc = documentImpl ?? (typeof document !== 'undefined' ? document : undefined);
    if (doc === undefined) return undefined;

    const onKeyDown = (event: globalThis.KeyboardEvent): void => {
      const isModP = (event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'p';
      if (isModP) {
        event.preventDefault();
        setGlobalPaletteOpen((prev) => !prev);
      }
    };
    doc.addEventListener('keydown', onKeyDown);
    return (): void => {
      doc.removeEventListener('keydown', onKeyDown);
    };
  }, [disableGlobalShortcut, documentImpl]);

  const setOpen = useCallback((open: boolean): void => {
    setGlobalPaletteOpen(open);
  }, []);

  return {
    slashOpen: slashState.slashOpen,
    slashQuery: slashState.slashQuery,
    globalPaletteOpen,
    setGlobalPaletteOpen: setOpen,
  };
}
